import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  DollarSign,
  Wrench,
  Search,
  Check,
  MessageSquare,
  HelpCircle,
  ChevronRight,
  X,
  Shield,
  Clock,
  Briefcase,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ticketService } from '../services/ticketService';
import { expenseService } from '../services/expenseService';
import Sidebar from '../components/Sidebar';
import FeedbackModal from '../components/FeedbackModal';
import PremiumUpgradeModal from '../components/PremiumUpgradeModal';

const ServiceProviderDashboard = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newExpense, setNewExpense] = useState({
    amount: '',
    description: '',
    category: 'labor'
  });
  const [showPremiumUpgrade, setShowPremiumUpgrade] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  useEffect(() => {
    if (currentUser && userData) {
      console.log('Setting up service provider dashboard for:', currentUser.uid);
      
      try {
        const unsubscribe = ticketService.subscribeToTickets(
          currentUser.uid,
          'serviceProvider',
          (tickets) => {
            console.log('Service provider tickets received:', tickets);
            setTickets(tickets || []);
            setLoading(false);
          }
        );

        return unsubscribe;
      } catch (error) {
        console.error('Error setting up ticket subscription:', error);
        setTickets([]);
        setLoading(false);
      }
    } else {
      console.log('No currentUser or userData:', { currentUser, userData });
      setLoading(false);
    }
  }, [currentUser, userData]);

  useEffect(() => {
    const calculateTotalEarnings = async () => {
      if (tickets.length > 0) {
        let total = 0;
        for (const ticket of tickets) {
          const ticketExpenses = await expenseService.getTicketTotalExpenses(ticket.id);
          total += ticketExpenses;
        }
        setTotalEarnings(total);
      }
    };

    calculateTotalEarnings();
  }, [tickets]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!selectedTicket) return;
    
    try {
      await expenseService.createExpense({
        ticketId: selectedTicket.id,
        amount: parseFloat(newExpense.amount),
        description: newExpense.description,
        category: newExpense.category,
        serviceProviderId: currentUser.uid,
        createdAt: new Date()
      });
      setShowAddExpense(false);
      setSelectedTicket(null);
      setNewExpense({ amount: '', description: '', category: 'labor' });
      alert('Expense added successfully!');
    } catch (error) {
      console.error('Error adding expense:', error);
      alert('Error adding expense: ' + error.message);
    }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      await ticketService.updateTicket(ticketId, { status: newStatus });
      alert('Ticket status updated successfully!');
    } catch (error) {
      console.error('Error updating ticket status:', error);
      alert('Error updating ticket status: ' + error.message);
    }
  };

  const handlePremiumUpgradeSuccess = () => {
    // Refresh user data to show premium status
    window.location.reload();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'in-progress':
        return 'bg-slate-100 text-slate-800 border-slate-300';
      case 'completed':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'closed':
        return 'bg-slate-50 text-slate-500 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'medium':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'low':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-600 mx-auto" />
          <p className="mt-4 text-sm font-medium text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <Sidebar />

        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10 w-full">
            <div className="flex items-center justify-between px-8 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <LayoutDashboard className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-slate-800">{t('dashboard.serviceProvider.title')}</h1>
                  <p className="text-slate-500 text-sm">Manage your jobs and earnings</p>
                </div>
                {userData?.accountType === 'premium' && (
                  <div className="flex items-center gap-2 bg-slate-800 text-white px-3 py-1.5 rounded-full text-xs font-medium">
                    <Shield className="w-3.5 h-3.5" strokeWidth={1.75} />
                    <span>Premium</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFeedbackModal(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors"
                  title={t('feedback.submit')}
                >
                  <MessageSquare className="w-4 h-4" strokeWidth={1.75} />
                  <span className="hidden sm:inline">{t('feedback.submit')}</span>
                </button>
                <div className="text-sm text-slate-500 pl-3 border-l border-slate-200">
                  {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                </div>
              </div>
            </div>
          </header>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-slate-800 rounded-lg p-5 text-white shadow-sm border border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                      <span className="text-sm font-semibold uppercase tracking-wide">
                        {userData?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-300">Welcome back</p>
                      <p className="font-semibold text-slate-50">{userData?.displayName?.split(' ')[0] || 'Provider'}</p>
                      {userData?.accountType === 'premium' && (
                        <div className="flex items-center gap-1 mt-1">
                          <Shield className="w-3 h-3 text-slate-400" strokeWidth={1.75} />
                          <span className="text-xs text-slate-400 font-medium">Premium</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums">{tickets.length}</p>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mt-0.5">Assigned Jobs</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums">
                      {tickets.filter(t => t.status === 'in-progress').length}
                    </p>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mt-0.5">In Progress</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>

              <Link
                to="/job-explore"
                className="bg-white rounded-lg shadow-sm border border-slate-200 p-5 flex items-center justify-between hover:border-slate-300 hover:shadow-md transition-colors group"
              >
                <div>
                  <p className="text-2xl font-semibold text-slate-800 tabular-nums group-hover:text-slate-600">Explore</p>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mt-0.5">Open to Bid</p>
                </div>
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                  <Search className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                </div>
              </Link>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums">${totalEarnings.toFixed(2)}</p>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mt-0.5">Total Earnings</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <h3 className="text-base font-semibold text-slate-800 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 gap-3">
                  <Link
                    to="/job-explore"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    <Search className="w-4 h-4" strokeWidth={1.75} />
                    Explore Open Jobs
                  </Link>
                  <button
                    onClick={() => {
                      if (tickets.length > 0) {
                        setSelectedTicket(tickets[0]);
                        setShowAddExpense(true);
                      } else {
                        alert('No tickets available to add expenses to.');
                      }
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-600 transition-colors w-full"
                  >
                    <DollarSign className="w-4 h-4" strokeWidth={1.75} />
                    Add Expense
                  </button>
                  <Link
                    to="/tickets"
                    className="flex items-center justify-center gap-2 px-4 py-3 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <ClipboardList className="w-4 h-4" strokeWidth={1.75} />
                    View All Jobs
                    <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
                  </Link>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                <h3 className="text-base font-semibold text-slate-800 mb-4">Completed Jobs</h3>
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Check className="w-7 h-7 text-slate-600" strokeWidth={1.75} />
                  </div>
                  <p className="text-3xl font-bold text-slate-800 tabular-nums">
                    {tickets.filter(t => t.status === 'completed').length}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">Jobs completed this month</p>
                </div>
              </div>
            </div>

            <Link
              to="/profile"
              className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 mb-6 flex items-center gap-4 hover:border-slate-300 hover:shadow transition-colors group"
            >
              <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 text-slate-700 font-semibold text-sm">
                {userData?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'P'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800 truncate">{userData?.displayName || 'Your profile'}</p>
                <p className="text-xs text-slate-500 truncate">
                  {[userData?.specialty, userData?.company].filter(Boolean).join(' · ') || 'Complete your profile'}
                </p>
              </div>
              <span className="text-xs font-medium text-slate-600 group-hover:text-slate-800 shrink-0 flex items-center gap-1">
                Manage profile
                <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
              </span>
            </Link>

            {userData?.accountType === 'basic' && (
              <div className="bg-slate-50 rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" strokeWidth={1.75} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-slate-800">Upgrade to Premium</h3>
                      <p className="text-sm text-slate-600">Unlock premium features and priority access</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-slate-800">$99</div>
                    <div className="text-xs text-slate-500">One-time payment</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-medium text-slate-800 mb-3">Premium Benefits:</h4>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-slate-600 shrink-0" strokeWidth={1.75} />
                        Verified professional badge
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-slate-600 shrink-0" strokeWidth={1.75} />
                        Priority access to leads & job tickets
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-slate-600 shrink-0" strokeWidth={1.75} />
                        Enhanced business profile
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-slate-600 shrink-0" strokeWidth={1.75} />
                        Advanced analytics & reporting
                      </li>
                    </ul>
                  </div>
                  <div className="flex flex-col justify-center">
                    <button
                      onClick={() => setShowPremiumUpgrade(true)}
                      className="w-full bg-slate-800 text-white py-3 px-6 rounded-lg font-medium hover:bg-slate-700 transition-colors"
                    >
                      Upgrade Now
                    </button>
                    <p className="text-xs text-slate-500 text-center mt-2">
                      No monthly fees • Cancel anytime
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <Search className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  Open to Bid Jobs
                </h3>
                <Link
                  to="/job-explore"
                  className="text-slate-600 text-sm font-medium hover:text-slate-800 transition-colors"
                >
                  Explore All
                </Link>
              </div>
              <div className="text-center py-8">
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Search className="w-7 h-7 text-slate-600" strokeWidth={1.75} />
                </div>
                <h3 className="text-base font-semibold text-slate-800 mb-2">Discover New Opportunities</h3>
                <p className="text-sm text-slate-600 mb-4">Browse tickets that property owners have opened to bidding and submit your proposals.</p>
                <Link
                  to="/job-explore"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors"
                >
                  <Search className="w-4 h-4" strokeWidth={1.75} />
                  Explore Open Jobs
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  Assigned Jobs
                </h3>
                <Link
                  to="/tickets"
                  className="text-slate-600 text-sm font-medium hover:text-slate-800 transition-colors"
                >
                  View All
                </Link>
              </div>
              {tickets.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <ClipboardList className="w-7 h-7 text-slate-600" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-base font-semibold text-slate-800 mb-2">No assigned jobs</h3>
                  <p className="text-sm text-slate-600">You'll see jobs here when property owners assign them to you.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tickets.slice(0, 5).map((ticket) => (
                    <div key={ticket.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center shrink-0">
                        <Wrench className="w-5 h-5 text-white" strokeWidth={1.75} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/tickets/${ticket.id}`}
                          className="text-sm font-medium text-slate-800 hover:text-slate-600 transition-colors block truncate"
                        >
                          {ticket.title || 'Untitled Job'}
                        </Link>
                        <p className="text-xs text-slate-500 truncate">{ticket.description?.substring(0, 50)}...</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {ticket.status === 'open' && (
                          <button
                            onClick={() => handleUpdateTicketStatus(ticket.id, 'in-progress')}
                            className="text-slate-600 hover:text-slate-800 text-xs font-medium"
                          >
                            Start Work
                          </button>
                        )}
                        {ticket.status === 'in-progress' && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedTicket(ticket);
                                setShowAddExpense(true);
                              }}
                              className="text-slate-600 hover:text-slate-800 text-xs font-medium"
                            >
                              Add Expense
                            </button>
                            <button
                              onClick={() => handleUpdateTicketStatus(ticket.id, 'completed')}
                              className="text-slate-600 hover:text-slate-800 text-xs font-medium"
                            >
                              Complete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAddExpense && selectedTicket && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border border-slate-200 w-full max-w-md shadow-xl rounded-lg bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                Add Expense
              </h3>
              <button
                onClick={() => {
                  setShowAddExpense(false);
                  setSelectedTicket(null);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-4">Adding expense for: {selectedTicket.title}</p>
            <form onSubmit={handleAddExpense} className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                  Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                  Description
                </label>
                <textarea
                  required
                  rows={3}
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                  placeholder="Description of the expense"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                  Category
                </label>
                <select
                  value={newExpense.category}
                  onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                >
                  <option value="labor">Labor</option>
                  <option value="materials">Materials</option>
                  <option value="equipment">Equipment</option>
                  <option value="travel">Travel</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddExpense(false);
                    setSelectedTicket(null);
                  }}
                  className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Add Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Premium Upgrade Modal */}
      {showPremiumUpgrade && (
        <PremiumUpgradeModal onClose={() => setShowPremiumUpgrade(false)} onSuccess={handlePremiumUpgradeSuccess} />
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </div>
  );
};

export default ServiceProviderDashboard; 